/**
 * Helper class for normalizing filenames to ensure they are valid across different file systems
 */
export class BdFilenameNormalizer {
  /**
   * Characters that are not allowed in filenames across major operating systems
   * (Windows, macOS, Linux)
   */
  private static readonly ILLEGAL_FILENAME_CHARACTERS = [
    '/',
    '\\',
    ':',
    '*',
    '?',
    '"',
    '<',
    '>',
    '|',
  ];

  /**
   * Maximum filename length (excluding extension) to ensure compatibility
   * across different file systems
   */
  private static readonly MAX_FILENAME_LENGTH = 200;

  /**
   * Default maximum length of a single filename component (as opposed to the
   * full built filename, see `MAX_FILENAME_LENGTH`).
   */
  private static readonly MAX_FILENAME_COMPONENT_LENGTH = 50;

  /**
   * Normalizes a string to be used as a safe filename component
   * @param str - The string to normalize
   * @returns A sanitized string safe for use in filenames
   */
  public static normalizeFilenameComponent(str: string): string {
    if (!str || str.length === 0) {
      return 'export';
    }

    // Replace illegal characters with underscores
    let normalized = str;
    this.ILLEGAL_FILENAME_CHARACTERS.forEach((illegalChar) => {
      normalized = normalized.split(illegalChar).join('_');
    });

    // Replace multiple spaces with single space
    normalized = normalized.replace(/\s+/g, ' ');

    // Replace spaces with hyphens for better readability
    normalized = normalized.replace(/\s/g, '-');

    // Remove leading/trailing hyphens and underscores
    normalized = normalized.replace(/^[-_]+|[-_]+$/g, '');

    // Ensure the component is not too long
    if (normalized.length > 50) {
      normalized = normalized.substring(0, 50);
    }

    // Convert to lowercase for consistency
    normalized = normalized.toLowerCase();

    return normalized;
  }

  /**
   * Sanitizes a string for use as a filename component while preserving original casing.
   * Unlike `normalizeFilenameComponent`, this does not lowercase the result.
   * @param str - The string to sanitize
   * @param maxLength - Maximum length of the resulting component (default 50)
   * @returns A sanitized string safe for use in filenames with original casing preserved
   */
  public static sanitizeFilenameComponent(
    str: string,
    maxLength = this.MAX_FILENAME_COMPONENT_LENGTH,
  ): string {
    if (!str || str.length === 0) {
      return 'export';
    }

    let sanitized = str;
    this.ILLEGAL_FILENAME_CHARACTERS.forEach((c) => {
      sanitized = sanitized.split(c).join('_');
    });

    sanitized = sanitized.replace(/\s+/g, ' ').replace(/\s/g, '-');
    sanitized = sanitized.replace(/^[-_]+|[-_]+$/g, '');

    if (sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength);
    }

    return sanitized;
  }

  /**
   * Generates a timestamp string suitable for filenames
   * Format: YYYY-MM-DD_HH-mm-ss
   * @param date - Optional date object (defaults to current date/time)
   * @returns A formatted timestamp string
   */
  public static generateTimestamp(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  }

  /**
   * Builds a complete filename from components
   * Format: {part1}_{part2}_{timestamp}.{extension}
   * @param parts - Array of filename components
   * @param extension - File extension (without dot)
   * @param includeTimestamp - Whether to include timestamp (default: true)
   * @returns A complete normalized filename
   */
  public static buildFilename(
    parts: string[],
    extension: string,
    includeTimestamp = true,
  ): string {
    const normalizedParts = parts
      .filter((part) => part && part.length > 0)
      .map((part) => this.normalizeFilenameComponent(part));

    if (normalizedParts.length === 0) {
      normalizedParts.push('export');
    }

    if (includeTimestamp) {
      normalizedParts.push(this.generateTimestamp());
    }

    const basename = normalizedParts.join('_');

    // Ensure total length doesn't exceed maximum
    let finalBasename = basename;
    if (basename.length > this.MAX_FILENAME_LENGTH) {
      finalBasename = basename.substring(0, this.MAX_FILENAME_LENGTH);
    }

    return `${finalBasename}.${extension}`;
  }
}
